import { useState } from 'react'
import { useATMStore } from '../store/atmStore'
import type { SimulationScenario, CollectionStrategy, ATMZone, ATMModel } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

const SCENARIOS: { value: SimulationScenario; label: string; description: string }[] = [
  { value: 'cash_depletion_wave',    label: 'Ola de Agotamiento de Efectivo', description: 'Múltiples ATMs vaciando cassettes simultáneamente' },
  { value: 'card_reader_failure_rain', label: 'Lluvia de Fallos IDC',          description: 'Fallo masivo de lectores de tarjetas por zona' },
  { value: 'network_outage_east_zone', label: 'Corte de Red — Zona Este',      description: 'Pérdida de conectividad en región Este' },
  { value: 'so_mismatch_cascade',    label: 'Cascada de Errores SO',           description: 'Incompatibilidades de imagen de sistema operativo' },
  { value: 'full_network_stress_test', label: 'Stress Test Completo',          description: 'Carga máxima en toda la red de 933 ATMs' },
  { value: 'tourist_zone_pressure',  label: 'Presión Zona Turística',          description: 'Alta demanda transaccional en zonas turísticas (Este, Norte)' },
  { value: 'custom',                 label: 'Personalizado',                   description: 'Configuración manual de parámetros' },
]

const STRATEGIES: { value: CollectionStrategy; label: string }[] = [
  { value: 'snmp',   label: 'SNMP Traps' },
  { value: 'mqtt',   label: 'MQTT Broker' },
  { value: 'opex',   label: 'OPEX Operational' },
  { value: 'xfs',    label: 'XFS Events' },
  { value: 'hybrid', label: 'Hybrid (Recomendado)' },
]

const ALERT_PROFILES: { value: string; label: string; description: string; badge: string }[] = [
  { value: 'balanced',  label: 'Balanceado',     description: 'Mezcla realista: CDM 35% / IDC 20% / SNMP 20% / ISO 15%', badge: 'DEFAULT' },
  { value: 'cdm_heavy', label: 'CDM Heavy',      description: 'Enfoque en agotamiento de efectivo y cassettes (65%)',     badge: 'CASH'    },
  { value: 'idc_heavy', label: 'IDC Heavy',      description: 'Fallos masivos de lectores de tarjetas (65%)',              badge: 'CARD'    },
  { value: 'network',   label: 'Red / SNMP',     description: 'Cortes de conectividad y traps SNMP (65%)',                 badge: 'NET'     },
  { value: 'realistic', label: 'Realista',        description: 'Dominado por transacciones ISO 8583 (35%) con fallos raros', badge: 'OPS'   },
]

const SPEED_OPTIONS = [1, 5, 10, 30, 60, 100] as const
type SpeedOption = typeof SPEED_OPTIONS[number]

const ZONES: ATMZone[] = ['Distrito Nacional', 'Norte', 'Sur', 'Este']
const MODELS: ATMModel[] = ['NCR Selfserv 22', 'NCR Selfserv 82', 'NCR Selfserv 84', 'NCR Selfserv 88', 'GRG H22VL']

const SIM_API = import.meta.env.VITE_SIM_API_URL || 'http://localhost:8001'

export default function SimulationControl() {
  const C = useTheme()
  const { simulationRun, setSimulationRun, clearEvents } = useATMStore()

  const [scenario,           setScenario]           = useState<SimulationScenario>('cash_depletion_wave')
  const [zone,               setZone]               = useState<ATMZone | ''>('')
  const [selectedModels,     setSelectedModels]     = useState<ATMModel[]>([])
  const [speed,              setSpeed]              = useState<SpeedOption>(10)
  const [duration,           setDuration]           = useState(8)
  const [strategy,           setStrategy]           = useState<CollectionStrategy>('hybrid')
  const [chaos,              setChaos]              = useState(false)
  const [chaosProbability,   setChaosProbability]   = useState(0.05)
  const [alertProfile,       setAlertProfile]       = useState('balanced')
  const [eventRateMultiplier, setEventRateMultiplier] = useState(1.0)
  const [loading,            setLoading]            = useState(false)

  const toggleModel = (model: ATMModel) =>
    setSelectedModels(prev => prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model])

  // Derive state from either real API field (state) or mock field (status)
  const runState = (simulationRun?.state ?? simulationRun?.status) as string | undefined
  const isRunning = runState === 'RUNNING'
  const isPaused  = runState === 'PAUSED'
  const isActive  = isRunning || isPaused

  const startSimulation = async () => {
    setLoading(true)
    const config = {
      scenario,
      target_zone: zone || null,
      speed_multiplier: speed,
      duration_hours: duration,
      enable_chaos: chaos,
      chaos_probability: chaosProbability,
      alert_profile: alertProfile,
      event_rate_multiplier: eventRateMultiplier,
      // UI-only fields (ignored by engine but kept for SimulationConfig display)
      zone_filter: zone || null,
      model_filter: selectedModels.length > 0 ? selectedModels : null,
      collection_strategy: strategy,
      inject_chaos: chaos,
    }
    try {
      const res = await fetch(`${SIM_API}/api/v1/simulation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (res.ok) {
        const run = await res.json()
        setSimulationRun(run)
        clearEvents()
      } else {
        console.error('Start failed:', res.status, await res.text())
      }
    } catch (err) {
      console.error('Start error:', err)
    }
    setLoading(false)
  }

  const pauseResumeSimulation = async () => {
    if (!simulationRun) return
    const endpoint = isPaused ? 'resume' : 'pause'
    try {
      const res = await fetch(`${SIM_API}/api/v1/simulation/${simulationRun.run_id}/${endpoint}`, { method: 'POST' })
      if (res.ok) {
        const updated = await res.json()
        setSimulationRun(updated)
      }
    } catch (err) {
      console.error('Pause/resume error:', err)
    }
  }

  const stopSimulation = async () => {
    if (!simulationRun) return
    try {
      const res = await fetch(`${SIM_API}/api/v1/simulation/${simulationRun.run_id}/stop`, { method: 'POST' })
      if (res.ok) {
        const updated = await res.json()
        setSimulationRun(updated)
      }
    } catch (err) {
      console.error('Stop error:', err)
    }
  }

  return (
    <div style={{ padding: 20, background: C.card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

      {/* ── Left: Scenario + Alert Profile ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SectionLabel>Escenario &amp; Perfil de Alertas</SectionLabel>

        <FormGroup label="Escenario">
          <select value={scenario} onChange={e => setScenario(e.target.value as SimulationScenario)}
            disabled={isActive} style={selectStyle}>
            {SCENARIOS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
            {SCENARIOS.find(s => s.value === scenario)?.description}
          </div>
        </FormGroup>

        <FormGroup label="Perfil de Tipo de Alerta">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {ALERT_PROFILES.map(p => (
              <button key={p.value} onClick={() => !isActive && setAlertProfile(p.value)}
                disabled={isActive}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                  borderRadius: 6, cursor: isActive ? 'not-allowed' : 'pointer', textAlign: 'left',
                  background: alertProfile === p.value ? `${C.blue}18` : C.card2,
                  border: `1px solid ${alertProfile === p.value ? C.blue : C.border}`,
                  fontFamily: 'inherit',
                }}>
                <span style={{
                  minWidth: 38, textAlign: 'center', fontSize: 9, fontWeight: 800, padding: '2px 5px',
                  borderRadius: 4, letterSpacing: 0.4, background: alertProfile === p.value ? C.blue : C.card,
                  color: alertProfile === p.value ? '#fff' : C.muted,
                }}>{p.badge}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: alertProfile === p.value ? C.blue : C.text }}>{p.label}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, marginTop: 1 }}>{p.description}</div>
                </div>
              </button>
            ))}
          </div>
        </FormGroup>

        <FormGroup label="Densidad de Eventos">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="range" min={0.1} max={5} step={0.1} value={eventRateMultiplier}
              onChange={e => !isActive && setEventRateMultiplier(+e.target.value)}
              disabled={isActive} style={{ flex: 1, accentColor: C.orange }} />
            <span style={{ minWidth: 42, textAlign: 'right', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', fontSize: 13,
              color: eventRateMultiplier > 3 ? C.red : eventRateMultiplier > 1.5 ? C.orange : C.green }}>
              {eventRateMultiplier.toFixed(1)}×
            </span>
          </div>
          <div style={{ fontSize: 9.5, color: C.muted, marginTop: 3 }}>
            {eventRateMultiplier <= 0.3 ? 'Modo silencioso — muy pocos eventos'
              : eventRateMultiplier <= 0.8 ? 'Tranquilo — ideal para demos pausadas'
              : eventRateMultiplier <= 1.5 ? 'Normal — densidad operativa realista'
              : eventRateMultiplier <= 3.0 ? 'Intenso — pico de quincena'
              : 'Tormenta — estrés máximo de red'}
          </div>
        </FormGroup>

        <FormGroup label="Zona (opcional)">
          <select value={zone} onChange={e => setZone(e.target.value as ATMZone | '')}
            disabled={isActive} style={selectStyle}>
            <option value="">Todas las zonas</option>
            {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </FormGroup>

        <FormGroup label="Modelos (opcional)">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {MODELS.map(model => (
              <button key={model} onClick={() => !isActive && toggleModel(model)}
                style={{
                  padding: '3px 10px', borderRadius: 999, fontSize: 10, cursor: isActive ? 'not-allowed' : 'pointer',
                  background: selectedModels.includes(model) ? `${C.blue}20` : 'transparent',
                  border: `1px solid ${selectedModels.includes(model) ? C.blue : C.border}`,
                  color: selectedModels.includes(model) ? C.blue : C.muted,
                  fontFamily: 'inherit',
                }}>
                {model}
              </button>
            ))}
          </div>
        </FormGroup>
      </div>

      {/* ── Right: Speed, Duration, Chaos, Controls ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SectionLabel>Velocidad, Duración &amp; Control</SectionLabel>

        <FormGroup label="Multiplicador de Velocidad">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 5 }}>
            {SPEED_OPTIONS.map(s => (
              <button key={s} onClick={() => !isActive && setSpeed(s)}
                style={{
                  padding: '7px 0', borderRadius: 6, fontSize: 12, fontWeight: 700,
                  cursor: isActive ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: speed === s ? C.primary : C.card2,
                  border: `1px solid ${speed === s ? C.blue : C.border}`,
                  color: speed === s ? '#fff' : C.muted,
                }}>
                {s}×
              </button>
            ))}
          </div>
          <div style={{ fontSize: 9.5, color: C.muted, marginTop: 4 }}>
            {speed}× → {duration}h sim = {(duration * 60 / speed).toFixed(0)} min reales
          </div>
        </FormGroup>

        <FormGroup label="Duración (horas simuladas)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="range" min={1} max={72} value={duration}
              onChange={e => !isActive && setDuration(+e.target.value)}
              disabled={isActive} style={{ flex: 1 }} />
            <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', fontSize: 14, color: C.text }}>
              {duration}h
            </span>
          </div>
        </FormGroup>

        <FormGroup label="Estrategia de Colección">
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {STRATEGIES.map(s => (
              <button key={s.value} onClick={() => !isActive && setStrategy(s.value)}
                style={{
                  padding: '4px 9px', borderRadius: 6, fontSize: 10.5, cursor: isActive ? 'not-allowed' : 'pointer',
                  background: strategy === s.value ? C.primary : 'transparent',
                  border: `1px solid ${strategy === s.value ? C.blue : C.border}`,
                  color: strategy === s.value ? '#fff' : C.muted,
                  fontFamily: 'inherit',
                }}>
                {s.label}
              </button>
            ))}
          </div>
        </FormGroup>

        <FormGroup label="Inyección de Caos">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: chaos ? 8 : 0 }}>
            <button onClick={() => !isActive && setChaos(v => !v)} style={{
              width: 40, height: 22, borderRadius: 11, border: 'none', cursor: isActive ? 'not-allowed' : 'pointer',
              background: chaos ? C.orange : C.card2, position: 'relative', transition: 'background 0.2s',
            }}>
              <span style={{
                position: 'absolute', top: 3, left: chaos ? 21 : 3, width: 16, height: 16,
                borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
              }} />
            </button>
            <span style={{ fontSize: 12, color: chaos ? C.orange : C.muted }}>
              {chaos ? 'Habilitado' : 'Deshabilitado'}
            </span>
          </div>
          {chaos && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="range" min={0.01} max={0.5} step={0.01} value={chaosProbability}
                onChange={e => setChaosProbability(+e.target.value)} disabled={isActive} style={{ flex: 1 }} />
              <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.orange }}>
                {(chaosProbability * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </FormGroup>

        {/* Status & Action Buttons */}
        <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px', marginTop: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Estado</span>
            <span style={{
              padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: isRunning ? 'rgba(34,197,94,0.12)' : isPaused ? 'rgba(248,148,33,0.12)' : 'rgba(107,114,128,0.12)',
              color: isRunning ? C.green : isPaused ? C.orange : C.gray,
            }}>
              {runState ?? 'Sin ejecutar'}
            </span>
          </div>

          {simulationRun && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11, marginBottom: 12 }}>
              <span style={{ color: C.muted }}>Eventos emitidos</span>
              <span style={{ color: C.text, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, textAlign: 'right' }}>
                {(simulationRun.events_emitted ?? 0).toLocaleString()}
              </span>
              <span style={{ color: C.muted }}>Velocidad activa</span>
              <span style={{ color: C.text, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, textAlign: 'right' }}>
                {simulationRun.config?.speed_multiplier ?? '—'}×
              </span>
              <span style={{ color: C.muted }}>Perfil</span>
              <span style={{ color: C.blue, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, textAlign: 'right', fontSize: 10 }}>
                {((simulationRun.config as unknown) as Record<string, unknown>)?.alert_profile as string ?? 'balanced'}
              </span>
              <span style={{ color: C.muted }}>Run ID</span>
              <span style={{ color: C.muted, fontSize: 9, fontFamily: 'JetBrains Mono, monospace', textAlign: 'right' }}>
                {simulationRun.run_id.slice(-8)}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            {!isActive ? (
              <button onClick={startSimulation} disabled={loading} style={{
                flex: 1, background: loading ? 'rgba(38,78,114,0.4)' : C.green, color: '#fff',
                border: 'none', borderRadius: 7, padding: '9px', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontFamily: 'inherit', fontWeight: 700,
              }}>
                {loading ? 'Iniciando...' : '▶ Iniciar'}
              </button>
            ) : (
              <>
                <button onClick={pauseResumeSimulation} style={{
                  flex: 1, background: isPaused ? C.green : C.orange, color: isPaused ? '#fff' : '#4a2a07',
                  border: 'none', borderRadius: 7, padding: '9px', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'inherit', fontWeight: 700,
                }}>
                  {isPaused ? '▶ Reanudar' : '⏸ Pausar'}
                </button>
                <button onClick={stopSimulation} style={{
                  flex: 1, background: C.red, color: '#fff', border: 'none',
                  borderRadius: 7, padding: '9px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', fontWeight: 700,
                }}>
                  ⏹ Detener
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Small helpers ────────────────────────────────────────────────────────────

const selectStyle: React.CSSProperties = {
  width: '100%', background: '#232d3e', color: '#e7ecf3',
  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
  padding: '7px 10px', fontSize: 12, fontFamily: 'inherit',
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#02AFF0', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
      {children}
    </div>
  )
}

function FormGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ fontSize: 10, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5, fontWeight: 600 }}>
        {label}
      </label>
      {children}
    </div>
  )
}
